/**
 * Quick search — FR-B6, T-1.8.
 *
 * One call, five kinds, one flat list. The service returns each result's
 * `kind` and its id and **no path**: it does not know this app's routes and
 * must not learn them (`services/prodesk-service/app/api/search.py`). `pathFor`
 * below is where a kind becomes a URL, and it is the only place.
 *
 * **`meta.refused` is the caller's own permissions, said out loud.** A role
 * with no verb on `invoice` gets no invoices and a list of the kinds that were
 * skipped, so the box can say "you cannot search invoices" instead of implying
 * the firm has none. Rendering that is not optional politeness — an empty
 * result that looks like an empty firm is the bug this field exists to prevent.
 */

import { api } from '../api'

export type SearchKind = 'client' | 'person' | 'case' | 'invoice' | 'lead'

/** Why this row matched, for the line under the label. */
export type MatchedOn = 'pan' | 'gstin' | 'email' | 'mobile' | 'name' | 'number'

export interface SearchResult {
  kind: SearchKind
  id: string
  label: string | null
  /** A masked PAN, a total, an email — whatever identifies the row second. */
  detail: string | null
  status: string | null
  extra: Record<string, unknown>
  matched: MatchedOn
}

export interface SearchPayload {
  term: string
  results: SearchResult[]
  counts: Partial<Record<SearchKind, number>>
}

export interface SearchMeta {
  searched: SearchKind[]
  /** Kinds the caller holds no verb on. Not an error — a fact about them. */
  refused: SearchKind[]
  limit: number
  /** At least one kind filled its limit, so there is more behind the list. */
  limited: boolean
}

export const KIND_LABELS: Record<SearchKind, string> = {
  client: 'Client',
  person: 'Person',
  case: 'Case',
  invoice: 'Invoice',
  lead: 'Lead',
}

export const MATCH_LABELS: Record<MatchedOn, string> = {
  pan: 'matched PAN',
  gstin: 'matched GSTIN',
  email: 'matched email',
  mobile: 'matched mobile',
  number: 'matched number',
  name: '',
}

/**
 * Where a result opens.
 *
 * Two of the five have no detail route in this app: a person is worked from the
 * staff list and a lead opens in a dialog on `/leads` (see `router.tsx`, which
 * says why). Both therefore land on their list rather than on a deep link —
 * honest, and better than inventing a route that 404s. A deep link for either
 * is a task for whoever adds one, not something to fake here.
 */
export function pathFor(result: SearchResult): string {
  switch (result.kind) {
    case 'client':
      return `/clients/${result.id}`
    case 'case':
      return `/cases/${result.id}`
    case 'invoice':
      return `/billing/invoices/${result.id}`
    case 'person':
      return '/people'
    case 'lead':
      return '/leads'
  }
}

export const search = {
  run: (term: string, options: { kinds?: SearchKind[]; limit?: number } = {}) => {
    const params = new URLSearchParams({ q: term })
    if (options.kinds?.length) params.set('kinds', options.kinds.join(','))
    if (options.limit) params.set('limit', String(options.limit))
    return api<SearchPayload>(`/api/v1/search?${params.toString()}`)
  },
}

/** The envelope's `meta`, typed, with a total fallback. */
export function searchMeta(meta: Record<string, unknown> | undefined): SearchMeta {
  return {
    searched: Array.isArray(meta?.searched) ? (meta.searched as SearchKind[]) : [],
    refused: Array.isArray(meta?.refused) ? (meta.refused as SearchKind[]) : [],
    limit: typeof meta?.limit === 'number' ? meta.limit : 5,
    limited: Boolean(meta?.limited),
  }
}

/**
 * The line under a result's label.
 *
 * Exported and pure because it is the one piece of wording here with a rule in
 * it: the match reason is shown **only when it is not the obvious one**. Every
 * search matches a name; saying so under every row is noise that hides the
 * rows where the match was a PAN.
 */
export function resultSubtitle(result: SearchResult): string {
  const parts = [KIND_LABELS[result.kind]]
  if (result.detail) parts.push(result.detail)
  const matched = MATCH_LABELS[result.matched]
  if (matched) parts.push(matched)
  return parts.join(' · ')
}

/**
 * Whether a term is long enough to send.
 *
 * Mirrors `MIN_TERM` in `app/repos/search.py`. The service refuses a shorter
 * one with a named field; this is what stops the box firing a request per
 * keystroke that it knows will be refused.
 */
export const MIN_TERM = 2

export function isSearchable(term: string): boolean {
  return term.trim().length >= MIN_TERM
}
